// Saving media runs several independent Bunny file operations after the
// record itself is written (active file move, thumbnail, archived files,
// deletions). Each retries transient failures (network drops, timeouts,
// 5xx) on its own, and all of them run even if one fails, so a single
// hiccup can't leave the rest of the save undone. Anything that still fails
// is reported by name.
const SAVE_FILE_OP_MAX_ATTEMPTS = 3;

const isTransientFileOpError = (error) => {
  const status = Number(error?.status);
  if (!Number.isFinite(status) || status === 0) return true;
  return status === 408 || status === 429 || status >= 500;
};

export async function runWithFileOpRetry(run, { delayMs = 1000 } = {}) {
  for (let attempt = 1; ; attempt += 1) {
    try {
      return await run();
    } catch (error) {
      if (
        attempt >= SAVE_FILE_OP_MAX_ATTEMPTS ||
        !isTransientFileOpError(error)
      ) {
        throw error;
      }
      await new Promise((resolve) =>
        setTimeout(resolve, delayMs * 2 ** (attempt - 1)),
      );
    }
  }
}

export async function runSaveFileOperations(tasks, options) {
  const results = await Promise.allSettled(
    tasks.map((task) => runWithFileOpRetry(task.run, options)),
  );
  const failures = results
    .map((result, index) => ({ result, label: tasks[index].label }))
    .filter(({ result }) => result.status === "rejected");
  if (failures.length > 0) {
    // eslint-disable-next-line no-console
    failures.forEach(({ label, result }) =>
      console.error(`[Media] ${label} failed:`, result.reason),
    );
    const labels = failures.map(({ label }) => label);
    const error = new Error(
      `${labels.join(", ")} could not be saved (${
        failures[0].result.reason?.message || "unknown error"
      })`,
    );
    error.failedFileOps = labels;
    throw error;
  }
}
